import { useEffect, useRef } from 'react';
import { useMediaSignedUrl } from '@/data/media';
import { cn } from '@/lib/cn';

interface VideoPlayerProps {
  assetId: string;
  /**
   * Watermark text overlaid on the video (typically the viewer's name +
   * timestamp). The framework's anti-download stance is enforced via:
   *   1. controlsList="nodownload noremoteplayback noplaybackrate"
   *   2. disablePictureInPicture
   *   3. context-menu suppression
   *   4. translucent CSS overlay watermark (per plan §6: "CSS overlay only")
   *   5. Signed-URL 1h TTL keeps the URL itself low-value
   * None of this stops a determined user with devtools — but the framework
   * explicitly scopes this as "deter casual download" (§12 out of scope:
   * server-side transcoding / DRM watermarking).
   */
  watermark?: string | undefined;
  className?: string | undefined;
}

export function VideoPlayer({ assetId, watermark, className }: VideoPlayerProps) {
  const signed = useMediaSignedUrl(assetId);
  const ref = useRef<HTMLVideoElement>(null);

  // Refresh the <video> source when the signed URL rotates. Setting .src
  // directly avoids a React-driven re-mount that would reset playback.
  useEffect(() => {
    const el = ref.current;
    if (!el || !signed.data?.url) return;
    if (el.src !== signed.data.url) {
      el.src = signed.data.url;
    }
  }, [signed.data?.url]);

  if (signed.isLoading) {
    return (
      <div
        aria-live="polite"
        className={cn(
          'surface flex aspect-video items-center justify-center text-[var(--color-ink-muted)]',
          className,
        )}
      >
        Loading video…
      </div>
    );
  }

  if (signed.isError || !signed.data?.url) {
    return (
      <div
        className={cn(
          'surface flex aspect-video items-center justify-center text-[var(--color-danger)]',
          className,
        )}
      >
        Couldn't load video.
      </div>
    );
  }

  return (
    <div className={cn('relative overflow-hidden rounded-[var(--radius-md)]', className)}>
      <video
        ref={ref}
        controls
        // controlsList strips the kebab download menu in Chromium / Edge /
        // Safari 16.4+. iOS Safari before 16.4 ignores controlsList and
        // will still show a download affordance. The framework explicitly
        // scopes anti-download as "CSS overlay only" (plan §12) — none of
        // this is a security boundary, only friction. devtools network
        // tab will always show the URL.
        controlsList="nodownload noremoteplayback noplaybackrate"
        disablePictureInPicture
        // Right-click "Save video as…" is gone with this. Again — discovery
        // surface only, not protection.
        onContextMenu={(e) => e.preventDefault()}
        // Non-standard `x-webkit-airplay="deny"` — suppresses the iOS
        // AirPlay affordance that would otherwise let the viewer cast to
        // a TV (and screen-record from there). Set via spread so the JSX
        // typechecker doesn't reject the kebab-case attr name.
        {...({ 'x-webkit-airplay': 'deny' } as Record<string, string>)}
        className="aspect-video w-full bg-black"
      >
        Your browser doesn't support HTML5 video.
      </video>
      {watermark && (
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 flex items-end justify-end p-3"
        >
          <span className="rounded-[var(--radius-sm)] bg-black/40 px-2 py-1 text-[var(--text-xs)] text-white/70 tabular-nums">
            {watermark}
          </span>
        </div>
      )}
    </div>
  );
}
